import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, FileText, Loader2, Trash2, Upload } from 'lucide-react';
import { Button } from '../../ui/button';
import { personenApi, type TeacherDocument } from '../../../lib/personen';
import { openTeacherInvoiceDocument } from '../../../lib/api/teacherInvoices';
import { formatDateTime } from '../../../lib/dateFormat';

export default function TeacherDocumentsSection({ wpUserId }: { wpUserId: number }) {
  const [items, setItems] = useState<TeacherDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    if (wpUserId <= 0) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const docs = await personenApi.getTeacherDocuments(wpUserId);
      setItems(Array.isArray(docs) ? docs : []);
    } catch (e) {
      setItems([]);
      setError(e instanceof Error ? e.message : 'Dokumente konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [wpUserId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onChooseFile = () => {
    fileInputRef.current?.click();
  };

  const onFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploading(true);
    setError(null);
    try {
      const created = await personenApi.uploadTeacherDocument(wpUserId, file);
      setItems((prev) => [created, ...prev]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload fehlgeschlagen.');
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (docId: string) => {
    setDeletingId(docId);
    setError(null);
    try {
      await personenApi.deleteTeacherDocument(wpUserId, docId);
      setItems((prev) => prev.filter((doc) => doc.id !== docId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {loading ? 'Dokumente werden geladen…' : `${items.length} Dokument${items.length === 1 ? '' : 'e'}`}
        </p>
        <Button onClick={onChooseFile} disabled={uploading || wpUserId <= 0} className="gap-2">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Dokument hochladen
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={onFileSelected}
          accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
        />
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Lade Dokumente…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border p-8 text-center">
          <FileText className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">Noch keine Dokumente hochgeladen.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((doc) => (
            <div key={doc.id} className="flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="font-medium truncate">{doc.name}</p>
                <p className="text-xs text-muted-foreground">
                  {doc.uploadedAt ? formatDateTime(doc.uploadedAt) : 'Unbekanntes Upload-Datum'}
                  {typeof doc.size === 'number' ? ` · ${(doc.size / 1024).toFixed(1)} KB` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  // Das Backend liefert eine data:-URL; die öffnen Browser nicht als Seite,
                  // der Tab blieb leer (T-0265). Wie bei den Lehrerrechnungen als Blob öffnen.
                  onClick={() => openTeacherInvoiceDocument(doc.url)}
                >
                  <Download className="h-3.5 w-3.5" />
                  Öffnen
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-destructive"
                  disabled={deletingId === doc.id}
                  onClick={() => void onDelete(doc.id)}
                >
                  {deletingId === doc.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                  Entfernen
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
